<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <div class="player-title">
              <Icon name="mdi:account-circle" class="player-icon" />
              <div>
                <h2>{{ playerData?.playerName }}</h2>
                <p v-if="playerData?.divisions[0]?.teamName" class="team-name">
                  <Icon name="mdi:shield" />
                  {{ playerData.divisions[0].teamName }}
                </p>
              </div>
            </div>
            <button @click="closeModal" class="close-button" aria-label="Close">
              <Icon name="mdi:close" />
            </button>
          </div>

          <div class="modal-body">
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <Icon name="mdi:loading" class="spin" />
              <p>Loading player data...</p>
            </div>

            <!-- Player Data -->
            <div v-else-if="playerData" class="player-content">
              <!-- Quick Stats -->
              <div class="quick-stats">
                <div class="stat-card">
                  <Icon name="mdi:chart-line" />
                  <div class="stat-info">
                    <span class="stat-label">Fargo Rating</span>
                    <span class="stat-value">{{ playerData.divisions[0]?.fargoRate || 'N/A' }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <Icon name="mdi:percent" />
                  <div class="stat-info">
                    <span class="stat-label">Overall Win %</span>
                    <span class="stat-value">{{ getWinPercentage(playerData) }}%</span>
                  </div>
                </div>
                <div class="stat-card">
                  <Icon name="mdi:trophy" />
                  <div class="stat-info">
                    <span class="stat-label">Total Games Won</span>
                    <span class="stat-value">{{ getTotalGamesWon(playerData) }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <Icon name="mdi:tournament" />
                  <div class="stat-info">
                    <span class="stat-label">Divisions</span>
                    <span class="stat-value">{{ Object.keys(getStatsForDisplay(playerData)).length }}</span>
                  </div>
                </div>
                <div class="stat-card">
                  <Icon name="mdi:calendar-check" />
                  <div class="stat-info">
                    <span class="stat-label">Total Matches</span>
                    <span class="stat-value">{{ playerData.matches.length }}</span>
                  </div>
                </div>
              </div>

              <!-- Tabs -->
              <div class="detail-tabs">
                <button
                  @click="activeTab = 'stats'"
                  :class="{ active: activeTab === 'stats' }"
                  class="tab-button"
                >
                  <Icon name="mdi:chart-bar" />
                  <span>Statistics</span>
                </button>
                <button
                  @click="activeTab = 'headToHead'"
                  :class="{ active: activeTab === 'headToHead' }"
                  class="tab-button"
                >
                  <Icon name="mdi:sword-cross" />
                  <span>Head-to-Head</span>
                </button>
                <button
                  @click="activeTab = 'matches'"
                  :class="{ active: activeTab === 'matches' }"
                  class="tab-button"
                >
                  <Icon name="mdi:history" />
                  <span>Match History</span>
                </button>
              </div>

              <!-- Tab Content -->
              <div class="tab-content">
                <!-- Statistics Tab -->
                <div v-if="activeTab === 'stats'" class="stats-tab">
                  <div v-if="Object.keys(getStatsForDisplay(playerData)).length > 1" class="info-banner">
                    <Icon name="mdi:information" />
                    <span>This player competes in {{ Object.keys(getStatsForDisplay(playerData)).length }} divisions. Stats shown per division below.</span>
                  </div>
                  <div v-for="(stats, divisionId) in getStatsForDisplay(playerData)" :key="divisionId" class="division-stats">
                    <h3 class="division-title">
                      <Icon name="mdi:tournament" />
                      {{ getDivisionName(playerData, String(divisionId)) }}
                    </h3>
                    <div class="stats-grid">
                      <div class="stat-item">
                        <span class="label">Rank</span>
                        <span class="value">#{{ stats.rank || 'N/A' }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="label">Win %</span>
                        <span class="value">{{ stats.winPercentage }}%</span>
                      </div>
                      <div class="stat-item">
                        <span class="label">Games Won</span>
                        <span class="value">{{ stats.gamesWon }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="label">W/L</span>
                        <span class="value">{{ stats.pointsForAgainst }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="label">Weeks</span>
                        <span class="value">{{ stats.weeks }}</span>
                      </div>
                      <div class="stat-item highlight">
                        <span class="label">Break & Runs</span>
                        <span class="value">{{ stats.breakAndRuns }}</span>
                      </div>
                      <div class="stat-item highlight">
                        <span class="label">Win Zips</span>
                        <span class="value">{{ stats.winZips }}</span>
                      </div>
                      <div class="stat-item highlight">
                        <span class="label">Table Runs</span>
                        <span class="value">{{ stats.tableRuns }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="label">Win on Break</span>
                        <span class="value">{{ stats.winOnBreak }}</span>
                      </div>
                      <div class="stat-item">
                        <span class="label">Consecutive Wins</span>
                        <span class="value">{{ stats.consecutiveWins }}</span>
                      </div>
                      <div class="stat-item highlight">
                        <span class="label">Perfect Matches</span>
                        <span class="value">{{ stats.perfectMatches }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Head-to-Head Tab -->
                <div v-if="activeTab === 'headToHead'" class="headtohead-tab">
                  <div v-if="Object.keys(playerData.headToHead).length === 0" class="empty-state">
                    <Icon name="mdi:account-alert" />
                    <p>No head-to-head data available</p>
                  </div>
                  <div v-else class="h2h-list">
                    <div
                      v-for="(record, opponentName) in sortedHeadToHead(playerData)"
                      :key="opponentName"
                      class="h2h-card"
                    >
                      <div class="h2h-header">
                        <div class="opponent-info">
                          <Icon name="mdi:account" />
                          <span class="opponent-name">{{ record.opponentName }}</span>
                        </div>
                        <div class="h2h-record">
                          <span class="wins">{{ record.wins }}W</span>
                          <span class="separator">-</span>
                          <span class="losses">{{ record.losses }}L</span>
                        </div>
                      </div>
                      <div class="h2h-stats">
                        <div class="h2h-stat">
                          <span class="h2h-label">Win %</span>
                          <span class="h2h-value" :class="getWinPercentageClass(record.winPercentage)">
                            {{ record.winPercentage }}%
                          </span>
                        </div>
                        <div class="h2h-stat">
                          <span class="h2h-label">Games</span>
                          <span class="h2h-value">{{ record.totalGames }}</span>
                        </div>
                        <div class="h2h-stat">
                          <span class="h2h-label">Matches</span>
                          <span class="h2h-value">{{ record.matchesPlayed }}</span>
                        </div>
                      </div>
                      <div v-if="record.recentForm.length > 0" class="recent-form">
                        <span class="form-label">Recent:</span>
                        <div class="form-indicators">
                          <span
                            v-for="(result, index) in record.recentForm"
                            :key="index"
                            class="form-badge"
                            :class="{ win: result === 'W', loss: result === 'L' }"
                          >
                            {{ result }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Match History Tab -->
                <div v-if="activeTab === 'matches'" class="matches-tab">
                  <div v-if="playerData.matches.length === 0" class="empty-state">
                    <Icon name="mdi:calendar-alert" />
                    <p>No match history available</p>
                  </div>
                  <div v-else class="match-list">
                    <div
                      v-for="match in playerData.matches"
                      :key="match.matchId"
                      class="match-card"
                    >
                      <div class="match-header">
                        <div class="match-date">
                          <Icon name="mdi:calendar" />
                          {{ formatDate(match.matchDate) }}
                        </div>
                        <NuxtLink :to="`/match/${match.matchId}`" class="view-match-link">
                          <Icon name="mdi:open-in-new" />
                          View Match
                        </NuxtLink>
                      </div>
                      <div class="match-teams">
                        <span :class="{ 'my-team': true }">{{ match.teamName }}</span>
                        <span class="vs">vs</span>
                        <span>{{ match.opponentTeamName }}</span>
                      </div>
                      <div class="match-result">
                        <div class="result-stats">
                          <span class="games-won">{{ match.gamesWon }} W</span>
                          <span class="games-lost">{{ match.gamesLost }} L</span>
                          <span class="total-games">({{ match.totalGames }} games)</span>
                        </div>
                        <div class="win-badge" :class="{ won: match.gamesWon > match.gamesLost, lost: match.gamesWon < match.gamesLost }">
                          {{ match.gamesWon > match.gamesLost ? 'WON' : match.gamesWon < match.gamesLost ? 'LOST' : 'TIED' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Error State -->
            <div v-else class="error-state">
              <Icon name="mdi:alert-circle" />
              <p>Player data not found</p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type { PlayerIndex, HeadToHeadRecord } from '~/composables/usePlayerIndex';

interface Props {
  isOpen: boolean;
  playerData: PlayerIndex | null;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<{
  close: [];
}>();

const activeTab = ref<'stats' | 'headToHead' | 'matches'>('stats');

// Reset tab when modal opens
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    activeTab.value = 'stats';
  }
});

const closeModal = () => {
  emit('close');
};

const handleOverlayClick = () => {
  closeModal();
};

const getWinPercentage = (player: PlayerIndex): number => {
  const allStats = Object.values(player.stats);
  
  // If no stats in the stats object, calculate from matches
  if (allStats.length === 0 && player.matches.length > 0) {
    const totalGames = player.matches.reduce((sum, m) => sum + m.totalGames, 0);
    const totalWins = player.matches.reduce((sum, m) => sum + m.gamesWon, 0);
    if (totalGames === 0) return 0;
    return Math.round((totalWins / totalGames) * 100);
  }
  
  if (allStats.length === 0) return 0;
  const avg = allStats.reduce((sum, s) => sum + s.winPercentage, 0) / allStats.length;
  return Math.round(avg);
};

const getTotalGamesWon = (player: PlayerIndex): number => {
  const statsValues = Object.values(player.stats);
  
  // If no stats in the stats object, calculate from matches
  if (statsValues.length === 0 && player.matches.length > 0) {
    return player.matches.reduce((sum, m) => sum + m.gamesWon, 0);
  }
  
  return statsValues.reduce((sum, s) => sum + s.gamesWon, 0);
};

const getDivisionName = (player: PlayerIndex, divisionId: string): string => {
  const division = player.divisions.find(d => d.divisionId === divisionId);
  return division?.divisionName || 'Unknown Division';
};

const getStatsForDisplay = (player: PlayerIndex) => {
  // If player has stats in the stats object, use those
  if (Object.keys(player.stats).length > 0) {
    return player.stats;
  }

  // Otherwise, calculate stats from matches grouped by division
  const calculatedStats: PlayerIndex['stats'] = {};
  
  player.matches.forEach(match => {
    if (!calculatedStats[match.divisionId]) {
      calculatedStats[match.divisionId] = {
        winPercentage: 0,
        gamesWon: 0,
        pointsForAgainst: '0/0',
        weeks: 0,
        breakAndRuns: 0,
        winZips: 0,
        tableRuns: 0,
        winOnBreak: 0,
        consecutiveWins: 0,
        perfectMatches: 0,
        teamName: match.teamName
      };
    }

    const divStats = calculatedStats[match.divisionId];
    divStats.gamesWon += match.gamesWon;
  });

  // Calculate win percentages and W/L for each division
  Object.entries(calculatedStats).forEach(([divisionId, stats]) => {
    const divisionMatches = player.matches.filter(m => m.divisionId === divisionId);
    const totalGames = divisionMatches.reduce((sum, m) => sum + m.totalGames, 0);
    const totalWins = divisionMatches.reduce((sum, m) => sum + m.gamesWon, 0);
    const totalLosses = divisionMatches.reduce((sum, m) => sum + m.gamesLost, 0);
    
    stats.winPercentage = totalGames > 0 ? Math.round((totalWins / totalGames) * 100) : 0;
    stats.pointsForAgainst = `${totalWins}/${totalLosses}`;
    stats.weeks = divisionMatches.length; // Use match count as weeks approximation
  });

  return calculatedStats;
};

const sortedHeadToHead = (player: PlayerIndex) => {
  return Object.fromEntries(
    Object.entries(player.headToHead).sort(([, a], [, b]) => b.totalGames - a.totalGames)
  );
};

const getWinPercentageClass = (winPct: number): string => {
  if (winPct >= 70) return 'excellent';
  if (winPct >= 50) return 'good';
  return 'poor';
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  overflow-y: auto;
}

.modal-container {
  background: linear-gradient(135deg, #1a2332 0%, #0f1621 100%);
  border: 2px solid rgba(255, 184, 28, 0.3);
  border-radius: 24px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 184, 28, 0.2);
}

.player-title {
  display: flex;
  align-items: center;
  gap: 16px;
}

.player-icon {
  font-size: 3rem;
  color: var(--gold);
}

.player-title h2 {
  font-size: 1.8rem;
  color: var(--cream);
  margin: 0;
}

.team-name {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--sky-blue);
  font-size: 1rem;
  margin: 4px 0 0 0;
}

.close-button {
  padding: 12px;
  background: rgba(255, 184, 28, 0.1);
  border: 1px solid rgba(255, 184, 28, 0.3);
  border-radius: 12px;
  color: var(--gold);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background: rgba(255, 184, 28, 0.2);
  transform: scale(1.1);
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

/* Loading/Error States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 64px 32px;
  color: var(--cream);
}

.loading-state svg,
.error-state svg {
  font-size: 3rem;
  color: var(--gold);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Quick Stats */
.quick-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.stat-card {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  border-color: var(--gold);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 184, 28, 0.2);
}

.stat-card svg {
  font-size: 2rem;
  color: var(--gold);
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-size: 0.85rem;
  color: rgba(233, 223, 213, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--cream);
}

/* Tabs */
.detail-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  border-bottom: 2px solid rgba(255, 184, 28, 0.2);
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: rgba(233, 223, 213, 0.6);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  bottom: -2px;
}

.tab-button:hover {
  color: var(--cream);
}

.tab-button.active {
  color: var(--gold);
  border-bottom-color: var(--gold);
}

.tab-button svg {
  font-size: 1.2rem;
}

/* Tab Content */
.tab-content {
  min-height: 300px;
}

/* Info Banner */
.info-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(41, 142, 195, 0.15);
  border: 1px solid rgba(41, 142, 195, 0.3);
  border-radius: 12px;
  color: var(--sky-blue);
  font-size: 0.95rem;
  margin-bottom: 16px;
}

.info-banner svg {
  font-size: 1.5rem;
  flex-shrink: 0;
}

/* Stats Tab */
.division-stats {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 184, 28, 0.1);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.division-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--gold);
  font-size: 1.3rem;
  margin-bottom: 20px;
}

.division-title svg {
  font-size: 1.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.stat-item {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 184, 28, 0.1);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-item.highlight {
  border-color: rgba(255, 184, 28, 0.3);
  background: rgba(255, 184, 28, 0.05);
}

.stat-item .label {
  font-size: 0.8rem;
  color: rgba(233, 223, 213, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-item .value {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--gold);
}

/* Head-to-Head Tab */
.h2h-list {
  display: grid;
  gap: 16px;
}

.h2h-card {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.h2h-card:hover {
  border-color: var(--gold);
  background: rgba(0, 0, 0, 0.4);
}

.h2h-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.opponent-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.opponent-info svg {
  color: var(--gold);
  font-size: 1.2rem;
}

.opponent-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--cream);
}

.h2h-record {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 1.2rem;
  font-weight: 700;
}

.wins {
  color: #22c55e;
}

.losses {
  color: #ef4444;
}

.separator {
  color: rgba(233, 223, 213, 0.5);
}

.h2h-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 12px;
}

.h2h-stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.h2h-label {
  font-size: 0.75rem;
  color: rgba(233, 223, 213, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.h2h-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--cream);
}

.h2h-value.excellent {
  color: #22c55e;
}

.h2h-value.good {
  color: var(--gold);
}

.h2h-value.poor {
  color: #ef4444;
}

.recent-form {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 184, 28, 0.1);
}

.form-label {
  font-size: 0.85rem;
  color: rgba(233, 223, 213, 0.7);
  font-weight: 500;
}

.form-indicators {
  display: flex;
  gap: 6px;
}

.form-badge {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  border: 1px solid;
}

.form-badge.win {
  background: rgba(34, 197, 94, 0.2);
  border-color: #22c55e;
  color: #22c55e;
}

.form-badge.loss {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
  color: #ef4444;
}

/* Match History Tab */
.match-list {
  display: grid;
  gap: 16px;
}

.match-card {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.match-card:hover {
  border-color: var(--gold);
  background: rgba(0, 0, 0, 0.4);
}

.match-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.match-date {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(233, 223, 213, 0.7);
  font-size: 0.9rem;
}

.view-match-link {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 184, 28, 0.1);
  border: 1px solid rgba(255, 184, 28, 0.3);
  border-radius: 8px;
  color: var(--gold);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.view-match-link:hover {
  background: rgba(255, 184, 28, 0.2);
  transform: translateX(4px);
}

.match-teams {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 1.1rem;
}

.my-team {
  color: var(--gold);
  font-weight: 700;
}

.vs {
  color: rgba(233, 223, 213, 0.5);
  font-style: italic;
}

.match-result {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-stats {
  display: flex;
  gap: 12px;
  align-items: center;
}

.games-won {
  color: #22c55e;
  font-weight: 700;
}

.games-lost {
  color: #ef4444;
  font-weight: 700;
}

.total-games {
  color: rgba(233, 223, 213, 0.6);
  font-size: 0.9rem;
}

.win-badge {
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  border: 1px solid;
}

.win-badge.won {
  background: rgba(34, 197, 94, 0.2);
  border-color: #22c55e;
  color: #22c55e;
}

.win-badge.lost {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
  color: #ef4444;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 64px 32px;
  color: rgba(233, 223, 213, 0.6);
}

.empty-state svg {
  font-size: 3rem;
  opacity: 0.5;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(20px);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-header {
    padding: 20px;
  }

  .player-title h2 {
    font-size: 1.4rem;
  }

  .modal-body {
    padding: 20px;
  }

  .quick-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .detail-tabs {
    gap: 0;
  }

  .tab-button {
    flex: 1;
    justify-content: center;
    padding: 12px 8px;
    font-size: 0.85rem;
  }

  .tab-button span {
    display: none;
  }

  .h2h-stats {
    flex-wrap: wrap;
  }

  .match-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
